import {useState} from 'react';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {MemberLifecycleDialogFrame} from '@client/pages/client/member-profile/MemberLifecycleDialogFrame';

interface TerminateMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  groupId: string;
  memberId: string; // composed `${subscriberId}-${memberSeq}`
  memberName: string;
  /**
   * Terminating a single dependent rather than the subscriber. Forces
   * `disenrollFamily: false` and hides the family checkbox, since disenrolling
   * the whole household from a dependent row is never the intent.
   */
  dependentOnly?: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);

export function TerminateMemberDialog({
  open,
  onClose,
  onSuccess,
  groupId,
  memberId,
  memberName,
  dependentOnly = false,
}: TerminateMemberDialogProps) {
  const {toast} = useToast();
  const [reasonId, setReasonId] = useState<string>('');
  const [disenrollDate, setDisenrollDate] = useState<string>(today());
  const [termDate, setTermDate] = useState<string>('');
  const [disenrollFamily, setDisenrollFamily] = useState(!dependentOnly);
  const [cobraEligible, setCobraEligible] = useState(false);
  const [notes, setNotes] = useState('');

  const {data: reasons, isLoading: reasonsLoading} =
    trpc.group.vbaMembers.listDisenrollReasons.useQuery(undefined, {enabled: open});

  const deactivate = trpc.group.vbaMembers.deactivate.useMutation({
    onSuccess: () => {
      toast({title: 'Member terminated', description: `${memberName} has been disenrolled.`});
      onSuccess();
      onClose();
    },
    onError: (err) =>
      toast({title: 'Termination failed', description: err.message, variant: 'destructive'}),
  });

  const handleSubmit = () => {
    if (!reasonId) {
      toast({
        title: 'Reason required',
        description: 'Select a disenroll reason.',
        variant: 'destructive',
      });
      return;
    }
    deactivate.mutate({
      groupId,
      id: memberId,
      reasonId: Number(reasonId),
      disenrollDate,
      disenrollFamily: dependentOnly ? false : disenrollFamily,
      cobraEligible,
      termDate: termDate || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <MemberLifecycleDialogFrame
      open={open}
      onClose={onClose}
      title={`Terminate ${memberName}`}
      description={
        dependentOnly
          ? 'Disenroll this dependent from coverage. The rest of the household is unaffected. This writes to VBA and is logged.'
          : 'Disenroll this member from coverage. This writes to VBA and is logged.'
      }
      cancelDisabled={deactivate.isPending}
      submitDisabled={deactivate.isPending}
      submitVariant='destructive'
      submitLabel={
        deactivate.isPending
          ? 'Terminating…'
          : dependentOnly
            ? 'Terminate dependent'
            : 'Terminate member'
      }
      onSubmit={handleSubmit}
    >
      <div className='space-y-1.5'>
        <Label>Disenroll reason</Label>
        <Select value={reasonId} onValueChange={setReasonId}>
          <SelectTrigger>
            <SelectValue placeholder={reasonsLoading ? 'Loading…' : 'Select a reason'} />
          </SelectTrigger>
          <SelectContent>
            {(reasons?.data ?? []).map((r) => (
              <SelectItem key={r.reasonId} value={String(r.reasonId)}>
                {r.description ?? `Reason ${r.reasonId}`}
                {r.cobraLength ? ` (COBRA ${r.cobraLength}mo)` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1.5'>
          <Label htmlFor='disenrollDate'>Disenroll date</Label>
          <Input
            id='disenrollDate'
            type='date'
            value={disenrollDate}
            onChange={(e) => setDisenrollDate(e.target.value)}
          />
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='termDate'>Term date (optional)</Label>
          <Input
            id='termDate'
            type='date'
            value={termDate}
            onChange={(e) => setTermDate(e.target.value)}
          />
        </div>
      </div>

      {!dependentOnly && (
        <div className='flex items-center gap-2'>
          <Checkbox
            id='disenrollFamily'
            checked={disenrollFamily}
            onCheckedChange={(c) => setDisenrollFamily(Boolean(c))}
          />
          <Label htmlFor='disenrollFamily' className='font-normal'>
            Disenroll entire family (subscriber + dependents)
          </Label>
        </div>
      )}

      <div className='flex items-center gap-2'>
        <Checkbox
          id='cobraEligible'
          checked={cobraEligible}
          onCheckedChange={(c) => setCobraEligible(Boolean(c))}
        />
        <Label htmlFor='cobraEligible' className='font-normal'>
          COBRA eligible
        </Label>
      </div>

      <div className='space-y-1.5'>
        <Label htmlFor='notes'>Notes (optional)</Label>
        <Textarea
          id='notes'
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder='Internal note for this termination…'
        />
      </div>
    </MemberLifecycleDialogFrame>
  );
}
